/**
 * LandingViews — one UTC day's count of arrivals on revia.to's landing, at
 * `workspaces/revia/landingViews/{day}` (`yyyy-mm-dd`). Written by the
 * `landingVisit` callable (`landingVisit.action.ts`); read by Revia staff.
 *
 * Counts only: no visitor is identified, nothing is stored on the device and
 * no record exists per visitor, so the day's document is the whole of what a
 * visit leaves behind.
 *
 * Each map is keyed by the counted value itself and capped at
 * `LANDING_VIEW_KEYS_MAX` distinct keys, so a caller sending a fresh referrer
 * or campaign on every call cannot grow the document past Firestore's limit:
 * a value arriving after the cap is counted in the map's `…Other` total
 * instead. The cap is checked on a read before the write, so concurrent first
 * arrivals can pass it by the handful in flight; the schema states no bound
 * on the key count for that reason.
 *
 * Fields justified:
 *   paths          — arrivals by landing path; the landing serves `/` alone,
 *                    so this is the day's total.
 *   referrers      — arrivals by the referring origin; a direct arrival
 *                    counts in `paths` only.
 *   referrersOther — arrivals from origins past the cap.
 *   utmSources     — arrivals by campaign source.
 *   utmSourcesOther — arrivals from sources past the cap.
 *
 * Excluded:
 *   utm medium / campaign — no reader counts by them yet; each would be
 *                    another capped map.
 *   day            — the doc id.
 *   visitor id, IP, user agent — the counts need none of them.
 *
 * Access: read and list for Revia staff signed in with a second factor (the
 * `mfa` claim), as every Revia record. No client write: the server counts.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { Buyer, type Workspace } from "@momo/types";
import { z } from "zod";
import { REVIA_ORG_ID, STAFF_PERMISSION } from "./staff.const.ts";

export const LANDING_VIEW_KEYS_MAX = 100;

export const LandingPathSchema = z
	.literal("/")
	.describe("the landing path the visitor arrived on");

export const ReferrerOriginSchema = z
	.url()
	.max(Buyer.REFERRER_MAX_LENGTH)
	.refine((value) => URL.canParse(value) && new URL(value).origin === value, {
		message: "an origin, without a path",
	})
	.describe("the origin of the site that sent the visitor here");

// Firestore reserves field names of the form `__…__`; a value counted as a
// map key may not take that shape.
export const UtmValueSchema = z
	.string()
	.trim()
	.min(1)
	.max(Buyer.UTM_VALUE_MAX_LENGTH)
	.refine((value) => !/^__.*__$/.test(value), {
		message: "a reserved field name",
	});

const Count = z.number().int().min(0);

export const LandingViewsSchema = z
	.object({
		paths: z.record(LandingPathSchema, Count),
		referrers: z.record(ReferrerOriginSchema, Count).nullish(),
		referrersOther: Count.nullish(),
		utmSources: z.record(UtmValueSchema, Count).nullish(),
		utmSourcesOther: Count.nullish(),
	})
	.strict();
export type LandingViews = z.infer<typeof LandingViewsSchema>;

const staff = { workspace: REVIA_ORG_ID };

export const LandingViews = entity<Workspace.Actor>()({
	schema: LandingViewsSchema,
	actions: ["read", "list"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, STAFF_PERMISSION, staff),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, STAFF_PERMISSION, staff),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: `workspaces/${REVIA_ORG_ID}/landingViews/{day}`,
		}),
	);
